import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plugs,
  CheckCircle,
  ArrowSquareOut,
  ArrowsClockwise,
  Copy,
  LinkBreak,
  Key,
  PencilSimple,
} from "@phosphor-icons/react";
import { SegTabs } from "@/components/SegTabs";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { QboMapping } from "@/components/QboMapping";
import { QboCustomers } from "@/components/QboCustomers";
import { QboHistory } from "@/components/QboHistory";
import { api, formatApiErrorDetail } from "@/lib/api";

const maskId = (id) =>
  !id ? "" : id.length <= 10 ? id : `${id.slice(0, 6)}…${id.slice(-4)}`;

export default function Integrations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("mapping");
  const [form, setForm] = useState({
    client_id: "",
    client_secret: "",
    environment: "production",
  });
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/qbo/status");
      setStatus(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not load status",
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the form's client id / environment in sync with the server when not editing.
  // When nothing is configured yet, keep the form default (production).
  useEffect(() => {
    if (status && !editing) {
      setForm((f) => ({
        ...f,
        client_id: status.client_id || "",
        environment: status.config_source
          ? status.environment || "production"
          : f.environment,
      }));
    }
  }, [status, editing]);

  useEffect(() => {
    const r = params.get("qbo");
    if (!r) return;
    if (r === "connected") toast.success("QuickBooks connected");
    else if (r === "denied") toast.error("Connection cancelled");
    else if (r === "badstate") toast.error("Session expired — try connecting again");
    else toast.error("Could not connect to QuickBooks");
    params.delete("qbo");
    setParams(params, { replace: true });
    load();
  }, [params, setParams, load]);

  const saveConfig = async () => {
    if (!form.client_id.trim()) {
      toast.error("Enter your Client ID");
      return;
    }
    if (!status?.has_secret && !form.client_secret.trim()) {
      toast.error("Enter your Client Secret");
      return;
    }
    setSaving(true);
    try {
      await api.post("/qbo/config", {
        client_id: form.client_id.trim(),
        client_secret: form.client_secret.trim(),
        environment: form.environment,
      });
      toast.success("Intuit keys saved");
      setEditing(false);
      setForm((f) => ({ ...f, client_secret: "" }));
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save keys",
      );
    }
    setSaving(false);
  };

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/qbo/connect");
      window.location.href = data.url;
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Add your Intuit keys first",
      );
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/qbo/disconnect");
      toast.success("QuickBooks disconnected");
      setConfirmDisconnect(false);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not disconnect",
      );
    }
    setBusy(false);
  };

  const backfill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/qbo/backfill");
      toast.success(`Synced ${data.invoices} invoices and ${data.payments} payments`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Backfill failed");
    }
    setBusy(false);
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied");
  };

  const inputCls =
    "w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-indigo-400/50";
  const labelCls = "mb-1 block text-xs uppercase tracking-widest text-zinc-500";

  const keyForm = (
    <div className="space-y-3" data-testid="qbo-key-form">
      <div>
        <label className={labelCls}>Environment</label>
        <select
          data-testid="qbo-environment"
          value={form.environment}
          onChange={(e) => setForm((f) => ({ ...f, environment: e.target.value }))}
          className={inputCls}
        >
          <option value="production">Production (live QuickBooks company)</option>
          <option value="sandbox">Sandbox (testing)</option>
        </select>
      </div>
      <div>
        <label className={labelCls}>Client ID</label>
        <input
          data-testid="qbo-client-id"
          value={form.client_id}
          onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
          placeholder="AB1cD…"
          className={`${inputCls} font-mono-data`}
        />
      </div>
      <div>
        <label className={labelCls}>Client Secret</label>
        <input
          data-testid="qbo-client-secret"
          type="password"
          value={form.client_secret}
          onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
          placeholder={status?.has_secret ? "•••••• saved — leave blank to keep" : "Paste your secret"}
          className={`${inputCls} font-mono-data`}
          autoComplete="off"
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <Button
          data-testid="qbo-save-keys"
          disabled={saving}
          onClick={saveConfig}
          className="gap-2 bg-indigo-500 text-white hover:bg-indigo-400 disabled:opacity-40"
        >
          <Key size={16} weight="bold" /> {saving ? "Saving…" : "Save keys"}
        </Button>
        {status?.configured && (
          <Button
            data-testid="qbo-cancel-edit"
            variant="outline"
            disabled={saving}
            onClick={() => {
              setEditing(false);
              setForm((f) => ({ ...f, client_secret: "" }));
            }}
            className="gap-2 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
          >
            Cancel
          </Button>
        )}
      </div>
      <p className="text-xs text-zinc-600">
        Keys are stored encrypted on the server. Also register the redirect URI below in
        your Intuit app under <b>Keys &amp; OAuth → Redirect URIs</b>.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <AppHeader title="Integrations" />
      <div className="mx-auto max-w-3xl px-4 py-8" data-testid="integrations-page">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
                <Plugs size={24} weight="bold" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">QuickBooks Online</h2>
                <p className="text-sm text-zinc-500">
                  Push invoices and paid deposits into your accounting automatically.
                </p>
              </div>
            </div>
            {status && (
              <span
                data-testid="qbo-connection-state"
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${
                  status.connected
                    ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                    : "border border-zinc-700 bg-zinc-800 text-zinc-400"
                }`}
              >
                {status.connected ? "Connected" : "Not connected"}
              </span>
            )}
          </div>

          {!status ? (
            <p className="mt-6 text-sm text-zinc-500">Loading…</p>
          ) : status.connected ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 text-emerald-300">
                  <CheckCircle size={18} weight="bold" />
                  <span className="text-sm font-semibold">
                    {status.company_name || "QuickBooks company"} ({status.environment})
                  </span>
                </div>
                <p className="mt-2 text-sm text-emerald-300/80">
                  New invoices and payments sync automatically. Synced so far:{" "}
                  {status.synced?.invoices || 0} invoices, {status.synced?.payments || 0}{" "}
                  payments.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  data-testid="qbo-backfill-button"
                  disabled={busy}
                  onClick={backfill}
                  className="gap-2 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
                >
                  <ArrowsClockwise size={16} weight="bold" /> Sync existing invoices
                </Button>
                {isAdmin && (
                  <Button
                    data-testid="qbo-disconnect-button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => setConfirmDisconnect(true)}
                    className="gap-2 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
                  >
                    <LinkBreak size={16} weight="bold" /> Disconnect
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-zinc-200">
                    <Key size={18} weight="bold" className="text-indigo-300" />
                    <span className="text-sm font-semibold">Intuit app keys</span>
                  </div>
                  {status.configured && !editing && (
                    <span
                      data-testid="qbo-keys-saved"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400"
                    >
                      <CheckCircle size={14} weight="bold" /> Saved
                    </span>
                  )}
                </div>

                {status.configured && !editing ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-zinc-400">
                      Environment <b className="text-zinc-200">{status.environment}</b>
                      {status.client_id && (
                        <>
                          {" · "}Client ID{" "}
                          <span className="font-mono-data text-zinc-300">
                            {maskId(status.client_id)}
                          </span>
                        </>
                      )}
                      {status.config_source === "env" && (
                        <span className="ml-2 text-xs text-zinc-600">(from server env)</span>
                      )}
                    </p>
                    <Button
                      data-testid="qbo-edit-keys"
                      variant="outline"
                      onClick={() => {
                        setEditing(true);
                        setForm((f) => ({ ...f, client_secret: "" }));
                      }}
                      className="gap-2 border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
                    >
                      <PencilSimple size={16} weight="bold" /> Edit keys
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3">{keyForm}</div>
                )}
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">
                  Redirect URI to register in your Intuit app
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <code
                    data-testid="qbo-redirect-uri"
                    className="flex-1 truncate rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 font-mono-data text-xs text-zinc-300"
                  >
                    {status.redirect_uri}
                  </code>
                  <Button
                    data-testid="qbo-copy-redirect"
                    variant="outline"
                    size="icon"
                    onClick={() => copy(status.redirect_uri)}
                    className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"
                  >
                    <Copy size={16} />
                  </Button>
                </div>
              </div>

              <Button
                data-testid="qbo-connect-button"
                disabled={busy || !status.configured}
                onClick={connect}
                className="gap-2 bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
              >
                <ArrowSquareOut size={16} weight="bold" />
                {busy ? "Opening…" : "Connect to QuickBooks"}
              </Button>
              {!status.configured && (
                <p className="text-xs text-zinc-600" data-testid="qbo-connect-hint">
                  Save your Intuit Client ID and Secret above to turn on the Connect button.
                </p>
              )}
            </div>
          )}
        </div>
        {status?.configured && (
          <div className="mt-6" data-testid="qbo-tabs-section">
            <SegTabs
              testId="qbo-tabs"
              tabs={[
                { key: "mapping", label: "Data mapping" },
                { key: "customers", label: "Customer matching" },
                { key: "history", label: "Sync history" },
              ]}
              value={tab}
              onChange={(t) => setTab(t.key)}
            />
            <div className="mt-4">
              {tab === "mapping" && <QboMapping connected={status.connected} />}
              {tab === "customers" && <QboCustomers connected={status.connected} />}
              {tab === "history" && <QboHistory />}
            </div>
          </div>
        )}
      </div>
      <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <DialogContent
          className="border-zinc-800 bg-zinc-900 sm:max-w-md"
          data-testid="qbo-disconnect-dialog"
        >
          <DialogHeader>
            <DialogTitle>Disconnect QuickBooks?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">
            New invoices and payments will stop syncing to{" "}
            <b className="text-zinc-200">{status?.company_name || "your QuickBooks company"}</b>{" "}
            until you reconnect. Your saved mapping is kept.
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <Button
              data-testid="qbo-disconnect-cancel"
              variant="outline"
              disabled={busy}
              onClick={() => setConfirmDisconnect(false)}
              className="border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              data-testid="qbo-disconnect-confirm"
              disabled={busy}
              onClick={disconnect}
              className="gap-2 bg-red-500 text-white hover:bg-red-400 disabled:opacity-40"
            >
              <LinkBreak size={16} weight="bold" /> Disconnect
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
